import React, { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import { useNavigate } from "react-router-dom";
import Toast from "react-bootstrap/Toast";
import Modal from "react-bootstrap/Modal";
import ConfirmModal from "../components/ConfirmModal";
import Cookies from "js-cookie";
import Thread from "../components/Thread";
import { Helmet } from "react-helmet-async";
import { createAvatar } from "@dicebear/core";
import { identicon } from "@dicebear/collection";
import { Splide, SplideSlide, SplideTrack } from "@splidejs/react-splide";
import "@splidejs/splide/dist/css/splide-core.min.css";
import { relativeTime, statusMeta, STATUS_STEPS } from "../utils/post";
import { useNavbarHeight } from "../utils/useNavbarHeight";
import HeroBubbles from "../components/HeroBubbles";
import ReactionBar from "../components/ReactionBar";
import ReactionsModal from "../components/ReactionsModal";
import { applyReaction } from "../utils/reactions";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Openers for the empty comments state, in the register people actually use on
// the feed rather than the instructional prompts that were here before. A
// suggestion you can send is worth more than an example you have to retype, so
// these are wired to the composer.
//
// Grouped by what the comment does. The questions are not filler: a report is
// far more useful with a shop, a date or a batch number attached, and asking
// for one is the single most valuable thing a reader can contribute. Nothing
// here names a brand or an agency, which keeps the set safe to seed with one
// click and valid on both the .ug and .ke domains.
//
// The count is free: HeroBubbles deals these from a shuffled deck, so there is
// no relationship to keep between the list length and its slot count. Kept to
// about 30 characters, which is what fits the right column at its 380px floor
// without ellipsing.
const COMMENT_STARTERS = [
  // Corroboration: has anyone else run into it
  { icon: "fa-solid fa-eye", text: "I've seen this too!" },
  { icon: "fa-solid fa-store", text: "I saw this at the market" },
  { icon: "fa-solid fa-rotate", text: "This happened to me too" },
  { icon: "fa-solid fa-hand", text: "I nearly bought one" },
  { icon: "fa-solid fa-people-group", text: "My neighbour bought this" },
  { icon: "fa-solid fa-location-crosshairs", text: "Same thing in my area" },
  { icon: "fa-solid fa-layer-group", text: "There is a lot of it around" },

  // Reaction
  { icon: "fa-solid fa-triangle-exclamation", text: "This is definitely fake" },
  { icon: "fa-solid fa-face-frown", text: "This is so bad" },
  { icon: "fa-solid fa-face-surprise", text: "Banange!" },
  { icon: "fa-solid fa-face-grin-tears", text: "This is so funny" },
  { icon: "fa-solid fa-skull-crossbones", text: "This one is dangerous" },
  { icon: "fa-solid fa-bullhorn", text: "People need to know this" },

  // Asking for the detail that makes a report actionable
  { icon: "fa-solid fa-location-dot", text: "Where did you buy it?" },
  { icon: "fa-solid fa-circle-question", text: "Which shop was this?" },
  { icon: "fa-solid fa-clock", text: "Is that shop still open?" },
  { icon: "fa-solid fa-tag", text: "How much did it cost?" },
  { icon: "fa-solid fa-receipt", text: "Do you have the receipt?" },
  { icon: "fa-solid fa-barcode", text: "What is the batch number?" },
  { icon: "fa-solid fa-calendar-day", text: "When did you buy it?" },
  { icon: "fa-solid fa-flag", text: "Did you report it to them?" },

  // Telling the fake from the real one
  { icon: "fa-solid fa-box-open", text: "The packaging looks off" },
  { icon: "fa-solid fa-not-equal", text: "The real one looks different" },
  { icon: "fa-solid fa-spell-check", text: "The label is misspelled" },
  { icon: "fa-solid fa-stamp", text: "The seal is not right" },
  { icon: "fa-solid fa-certificate", text: "Check the hologram" },
  { icon: "fa-solid fa-palette", text: "The colour is wrong" },
  { icon: "fa-solid fa-weight-hanging", text: "The weight feels wrong" },

  // Support and advice
  { icon: "fa-solid fa-heart", text: "Thanks for the warning" },
  { icon: "fa-solid fa-shield-halved", text: "Everyone please be careful" },
  { icon: "fa-solid fa-check-double", text: "Buy from trusted shops only" },
];

const orangePin = L.divIcon({
  className: "",
  html: `<svg width="28" height="38" viewBox="0 0 28 38" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 24 14 24S28 24.5 28 14C28 6.268 21.732 0 14 0z" fill="#e66b2c"/>
    <circle cx="14" cy="14" r="6" fill="white"/>
  </svg>`,
  iconSize: [28, 38],
  iconAnchor: [14, 38],
});

const Post = ({ refreshUserData }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [showCommentPostedToast, setShowCommentPostedToast] = useState(false);
  const [showCommentDeletedToast, setShowCommentDeletedToast] = useState(false);
  const [showDeletePost, setShowDeletePost] = useState(false);
  const [showMarkResolved, setShowMarkResolved] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showReactorsModal, setShowReactorsModal] = useState(false);

  const [userDataLoaded, setUserDataLoaded] = useState(false);
  const [userId, setUserId] = useState(null);
  const [userType, setUserType] = useState(null);
  const [userName, setUserName] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [userAnonymous, setUserAnonymous] = useState(null);
  const [displayName, setDisplayName] = useState("");

  const [comment, setComment] = useState("");
  const [commentInputVisible, setCommentInputVisible] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imageError, setImageError] = useState("");

  const handleImageSelection = (event) => {
    const files = event.target.files;
    if (files.length > 5) {
      setImageError("You can only select up to 5 attachments.");
      return;
    } else {
      setImageError("");
    }
    setSelectedImages(Array.from(files));
  };

  const handleRemoveImage = (index) => {
    // Remove the image from the selectedImages array
    setSelectedImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  // Opens the composer and seeds it with the suggestion that was clicked. It
  // is a starting point, not the comment: sending still takes a deliberate
  // press of Comment, and the text is editable in between.
  //
  // The field is only protected from being overwritten if its contents are the
  // reader's own. Testing "is it empty" alone cannot tell that apart from a
  // starter applied a moment ago, so the first click would win and every later
  // one would silently do nothing. Remembering what was last seeded is what
  // makes clicking a second suggestion swap to it while typed words survive.
  const composerRef = useRef(null);
  const seededRef = useRef("");
  const applyStarter = (text) => {
    setCommentInputVisible(true);
    if (!comment.trim() || comment === seededRef.current) {
      setComment(text);
      seededRef.current = text;
    }
    // autoFocus only fires on mount, so an already open composer needs this.
    composerRef.current?.focus();
  };

  useEffect(() => {
    if (userAnonymous === "true" && userId) {
      setDisplayName(`Bleeper#${userId.slice(-6)}`);
    } else {
      setDisplayName(userName);
    }
  }, [userId, userAnonymous, userName]);

  const [copyRefText, setCopyRefText] = useState("Copy Ref No.");

  // Which photo the viewer is on. Driven by Splide's own onMoved so the
  // thumbnails stay in step with drag, keyboard and arrow-button moves alike.
  const [shotIdx, setShotIdx] = useState(0);
  const splideRef = useRef(null);

  const avatar = React.useMemo(() => {
    if (!post || !post.displayname) return null;
    return createAvatar(identicon, {
      seed: post.displayname,
    }).toDataUri();
  }, [post?.displayname]);

  const useravatar = React.useMemo(() => {
    if (!displayName) return null;
    return createAvatar(identicon, {
      seed: displayName,
    }).toDataUri();
  }, [displayName]);

  const getUserData = () => {
    setUserDataLoaded(false);
    // Retrieve user data from cookies
    const userIdFromCookie = Cookies.get("userId");
    const userTypeFromCookie = Cookies.get("userType");
    const userNameFromCookie = Cookies.get("userName");
    const userPermissionsFromCookie = Cookies.get("userPermissions");
    const userAnonymousFromCookie = Cookies.get("userAnonymous");

    // Update state with user data
    setUserId(userIdFromCookie);
    setUserType(userTypeFromCookie);
    setUserName(userNameFromCookie);
    setUserPermissions(userPermissionsFromCookie);
    setUserDataLoaded(true);
    setUserAnonymous(userAnonymousFromCookie);
  };

  // navigate, not window.location: a full document load throws away the bundle
  // and repaints from the browser's blank canvas, which is the white flash
  // between tapping a reaction signed out and seeing the sign-in page.
  const handleShowSignIn = () => {
    navigate(`/signin?redirect=${encodeURIComponent(location.pathname)}`);
  };

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
  };
  const handleShowConfirmModal = () => {
    setShowConfirmModal(true);
  };

  const handleAuthComplete = () => {
    refreshUserData();
    getUserData();
  };

  // Mark post as resolved
  const handleProceed = () => {
    var myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    var raw = JSON.stringify({
      user: userId,
      post: post._id,
    });
    fetch(`/api/comments/resolved`, {
      method: "POST",
      headers: myHeaders,
      body: raw,
    })
      .then((response) => response.json())
      .then((data) => {
        handleCloseConfirmModal();
        fetchPost();
        fetchComments();
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  function convertDateTimeFormat(dateTimeString) {
    const dateTime = new Date(dateTimeString).toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
    return dateTime;
  }

  const fetchPost = () => {
    let url = `/api/posts/${id}`;

    if (userId) {
      url += `?user=${userId}`;
    }

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        setPost(data);
        checkMarkResolved(data);
        checkDeletePost(data);
      });
  };

  const checkMarkResolved = (postData) => {
    if (
      postData &&
      postData.status === "Ongoing action" &&
      ((userPermissions && userPermissions.includes("resolvePosts")) ||
        userType === "superadmin")
    ) {
      setShowMarkResolved(true);
    } else {
      setShowMarkResolved(false);
    }
  };

  // Held in a ref so the interval below is actually clearable - the previous
  // version shadowed its own variable and leaked one interval per fetch.
  const deleteWindowIntervalRef = useRef(null);

  const checkDeletePost = (postData) => {
    const timeDifferenceInMinutes = () =>
      Math.floor((new Date() - new Date(postData.dateTime)) / (1000 * 60));

    const clearDeleteWindow = () => {
      if (deleteWindowIntervalRef.current) {
        clearInterval(deleteWindowIntervalRef.current);
        deleteWindowIntervalRef.current = null;
      }
    };

    const checkTimeDifference = () => {
      if (timeDifferenceInMinutes() <= 5) {
        setShowDeletePost(true);
      } else {
        setShowDeletePost(false);
        clearDeleteWindow();
      }
    };

    clearDeleteWindow();

    if (
      (userPermissions && userPermissions.includes("deletePosts")) ||
      userType === "superadmin"
    ) {
      setShowDeletePost(true);
    } else if (userId !== undefined && postData.user === userId) {
      checkTimeDifference();
      deleteWindowIntervalRef.current = setInterval(checkTimeDifference, 60000);
    } else {
      setShowDeletePost(false);
    }
  };

  useEffect(
    () => () => {
      if (deleteWindowIntervalRef.current)
        clearInterval(deleteWindowIntervalRef.current);
    },
    [],
  );

  useEffect(() => {
    const fetchData = async () => {
      getUserData();
      if (userDataLoaded) {
        fetchPost();
        fetchComments();

        // Scroll to top
        window.scrollTo(0, 0);
      }
    };
    fetchData();
  }, [id, userId]);

  const deletePost = () => {
    fetch(`/api/posts/${id}`, {
      method: "DELETE",
    })
      .then((res) => {
        if (res.ok) {
          // Post deleted successfully, redirect to the home
          navigate(`/`);
        } else {
          // Handle error response
          console.error("Error deleting post:", res.statusText);
        }
      })
      .catch((error) => {
        // Handle fetch error
        console.error("Fetch error:", error);
      });
  };

  // The picker closes on the click, so the emoji has to have changed by then:
  // the counts move locally first and the request only reconciles if it fails.
  // Refetching on success would also re-run checkMarkResolved / checkDeletePost,
  // which have nothing to do with reacting.
  const react = (key) => {
    if (!userId) {
      handleShowSignIn();
    } else {
      setPost((current) => applyReaction(current, key));

      var myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      var raw = JSON.stringify({
        user: userId,
        post: post._id,
        reaction: key,
      });

      var requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      fetch("/api/votes/post", requestOptions)
        .then((response) => {
          if (!response.ok) throw new Error(`Reaction failed: ${response.status}`);
          return response.json();
        })
        .catch((error) => {
          console.error("Error:", error);
          fetchPost(); // put the counts back the way the server has them
        });
    }
  };

  const handleFollow = () => {
    if (!userId) {
      handleShowSignIn();
    } else {
      var myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      var raw = JSON.stringify({
        user: userId,
        post: post._id,
      });

      var requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      fetch("/api/followings", requestOptions)
        .then((response) => response.json())
        .then((data) => {
          fetchPost();
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    }
  };

  const [comments, setComments] = useState([]);
  const fetchComments = () => {
    fetch(`/api/comments/post/${id}`)
      .then((res) => res.json())
      .then((data) => setComments(Array.isArray(data) ? data : []))
      .catch((error) => console.error("Error fetching comments:", error));
  };

  const [replies, setReplies] = useState([]);
  const fetchReplies = () => {
    fetch(`/api/commentReplies/post/${id}`)
      .then((res) => res.json())
      .then((data) => setReplies(Array.isArray(data) ? data : []))
      .catch((error) => console.error("Error fetching comments:", error));
  };

  // Derived rather than stored: the old counter accumulated stale values every
  // time replies were refetched.
  const commentsCount = comments.length + replies.length;

  // fetch replies when comments change
  useEffect(() => {
    fetchReplies();
  }, [comments]);

  const handlePostComment = () => {
    fetchComments();
    fetchPost();
    setShowCommentPostedToast(true);
    // Absent until the first comment renders, so guard rather than assume.
    const thread = document.getElementById("bleepThread");
    if (thread) thread.scrollTop = 0;
  };

  const postComment = (event) => {
    if (!userId) {
      event.preventDefault();
      handleShowSignIn();
      return;
    }

    event.preventDefault();

    const formData = new FormData();

    // Append other form data fields
    formData.append("user", userId);
    formData.append("post", post._id);
    formData.append("comment", comment);

    // Append selected images to formData
    selectedImages.forEach((image) => {
      formData.append("commentimages", image);
    });

    fetch("/api/comments", {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        setComment("");
        setSelectedImages([]);
        handleImageSelection({ target: { files: [] } });
        setCommentInputVisible(false);
        handlePostComment();
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(post._id);
    setCopyRefText("Copied!");
    // The old version never reset, so the control read "Copied!" for the rest
    // of the session. Matches handleShare below.
    setTimeout(() => setCopyRefText("Copy Ref No."), 1500);
  };

  const [shareLabel, setShareLabel] = useState("Share");

  // handler
  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${post.displayname} posted a Bleep`,
          text: post.description || "Check out this Bleep",
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        setShareLabel("Copied!");
        setTimeout(() => setShareLabel("Share"), 1500);
      }
    } catch {
      // user cancelled or share failed silently
    }
  };

  const handleToggleCommentInput = () => {
    setCommentInputVisible(!commentInputVisible);
  };

  const status = statusMeta(post?.status);
  // Resolving a report closes its discussion. Named once because the composer,
  // the closed notice and the comment starters all key off it.
  const commentsOpen = post?.status !== "Resolved";

  const mapLat = post?.location?.coordinates?.[1];
  const mapLng = post?.location?.coordinates?.[0];
  const hasPin = typeof mapLat === "number" && typeof mapLng === "number";

  // The split reads the navbar's height, which is content-derived.
  useNavbarHeight();

  // From 992px up the split owns the viewport and the detail column is the only
  // scroller, so the document must not scroll too or the page shows two
  // scrollbars. Done here rather than in CSS because the theme file forbids
  // `body` selectors (Console.css owns body for the admin pages), and because
  // an inline lock is guaranteed to be lifted when this page unmounts.
  // On <html>, not <body>, and with no dependencies.
  //
  // react-bootstrap's modal manager owns document.body while a modal is up: it
  // records the body's inline overflow on open and writes that value back when
  // the exit transition ends. Re-running this effect on modal state — which is
  // what it used to do — lost that race in both directions. The cleanup fired
  // first and cleared the lock, so the manager recorded "" as the original, then
  // restored "" a few hundred milliseconds after close, overwriting the lock
  // this effect had already put back. The page kept its own scrollbar on top of
  // the detail column's.
  //
  // The manager never touches documentElement, so locking there sidesteps the
  // ordering entirely and the effect goes back to running once.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 992px)");
    const root = document.documentElement;
    const apply = () => {
      root.style.overflow = mq.matches ? "hidden" : "";
    };

    apply();
    mq.addEventListener("change", apply);
    return () => {
      mq.removeEventListener("change", apply);
      root.style.overflow = "";
    };
  }, []);

  // Bound once per gallery instead of on every render. Unbind rather than
  // destroy, so the two galleries do not tear each other down.
  useEffect(() => {
    Fancybox.bind("[data-fancybox='bleep-gallery']", {});
    return () => Fancybox.unbind("[data-fancybox='bleep-gallery']");
  }, [post?.imageUrls]);

  // Comment attachments. Bound here rather than inside Comment because the
  // selector is shared across every comment on the page: each instance binding
  // it on mount meant N delegations for one gallery, and each calling the
  // global Fancybox.destroy() on unmount meant deleting a single comment tore
  // the lightbox off the others and off the photos above.
  useEffect(() => {
    Fancybox.bind("[data-fancybox='comment-gallery']", {});
    return () => Fancybox.unbind("[data-fancybox='comment-gallery']");
  }, []);

  const isStaff = !(userType === "user" || userType == null);

  // react-router gives the first entry of a session the key "default", so this
  // is how we know a share link was opened cold: going back would leave the
  // site entirely, so fall through to the feed instead.
  const dismiss = () =>
    location.key === "default"
      ? navigate(isStaff ? "/dashboard" : "/")
      : navigate(-1);

  const dismissButton = (
    <button
      type="button"
      className="bl-close-btn post-dismiss"
      onClick={dismiss}
      aria-label="Close this bleep"
    >
      <i className="fas fa-xmark" aria-hidden="true" />
    </button>
  );
  const shots = post?.imageUrls ?? [];

  // "slide", never "loop": loop clones slides, and every clone would add a
  // duplicate entry to the Fancybox gallery bound above.
  const SHOT_OPTIONS = {
    type: "slide",
    rewind: true,
    perPage: 1,
    pagination: false,
    arrows: shots.length > 1,
    drag: shots.length > 1,
    keyboard: "focused",
  };

  if (!post) {
    return (
      <div className="bleep-light bl-page--dark post-page">
        <div className="post-mkt">
          <div className="post-mkt__media">
            <div className="post-shots post-skeleton-gallery bl-pulse" />
          </div>
          <div className="post-mkt__side">
            <div className="bl-pulse">
              <div
                className="post-skeleton-line"
                style={{ width: "50%", height: 20 }}
              />
              <div
                className="post-skeleton-line"
                style={{ width: "35%", height: 16 }}
              />
              <div className="post-skeleton-line" />
              <div className="post-skeleton-line" style={{ width: "70%" }} />
            </div>
            <p className="post-loading-label">Loading bleep</p>
          </div>
        </div>
      </div>
    );
  }

  // Lives in the status block, not the action row: you follow a report because
  // you want to know when that block changes. Kept as a variable because a post
  // whose status the server does not recognise renders no block, and following
  // has to survive that — it falls back into the action row.
  const followButton = (
    <button
      type="button"
      className={`post-action-btn${post.following == 1 ? " following" : ""}`}
      onClick={handleFollow}
      aria-pressed={post.following == 1}
    >
      <i
        className={`fas fa-${post.following == 1 ? "check" : "plus"}`}
        aria-hidden="true"
      />
      {post.following == 1 ? "Following" : "Follow"} · {post.followers}
    </button>
  );

  return (
    <>
      <Helmet>
        <title>{post.displayname} posted a Bleep</title>
        <meta name="description" content={post.description} />
        <meta
          property="og:title"
          content={post.displayname + " posted a Bleep"}
        />
        <meta property="og:description" content={post.description} />
        <meta property="og:image" content={post.thumbnail} />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      {/* bl-page--dark redeclares the token scale, so every .post-* rule
          resolves against the dark steps. */}
      <div className="bleep-light bl-page--dark post-page">
        <div className="post-mkt">
          {/* ------------------------------------------------- Media pane */}
          <div className="post-mkt__media">
            {shots.length === 0 ? (
              <div className="post-shots post-shots--empty">
                <i className="far fa-image" aria-hidden="true" />
              </div>
            ) : (
              <div className="post-shots">
                <Splide
                  hasTrack={false}
                  ref={splideRef}
                  options={SHOT_OPTIONS}
                  onMoved={(_splide, index) => setShotIdx(index)}
                  aria-label="Evidence photos"
                >
                  <SplideTrack>
                    {shots.map((url, index) => (
                      <SplideSlide key={url}>
                        <div className="post-shot">
                          {/* A blurred copy of the same photo fills the
                              letterbox, so `contain` never leaves bare
                              background. Decorative: the real image follows. */}
                          <div
                            className="post-shot__blur"
                            style={{ backgroundImage: `url("${url}")` }}
                            aria-hidden="true"
                          />
                          <img
                            className="post-shot__img bleep-image"
                            data-fancybox="bleep-gallery"
                            src={url}
                            alt={`Evidence ${index + 1} of ${shots.length}`}
                            loading={index === 0 ? "eager" : "lazy"}
                          />
                        </div>
                      </SplideSlide>
                    ))}
                  </SplideTrack>

                  {shots.length > 1 && (
                    <div className="splide__arrows post-shots__arrows">
                      <button
                        type="button"
                        className="splide__arrow splide__arrow--prev"
                        aria-label="Previous photo"
                      >
                        <i className="fas fa-chevron-left" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="splide__arrow splide__arrow--next"
                        aria-label="Next photo"
                      >
                        <i
                          className="fas fa-chevron-right"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  )}
                </Splide>

                {shots.length > 1 && (
                  <span className="post-shot-count">
                    {shotIdx + 1} / {shots.length}
                  </span>
                )}
              </div>
            )}

            {shots.length > 1 && (
              <div className="post-thumbs">
                {shots.map((url, index) => (
                  <button
                    key={url}
                    type="button"
                    className="post-thumb"
                    aria-current={index === shotIdx ? "true" : undefined}
                    aria-label={`Show photo ${index + 1}`}
                    onClick={() => splideRef.current?.go(index)}
                  >
                    <img src={url} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ------------------------------------------------ Detail column */}
          <div className="post-mkt__side">
            {/* Always rendered now that it carries the way out of the page, and
                sticky on desktop where this column is its own scroll container:
                otherwise the only dismiss control scrolls away with the
                comments. */}
            <div className="post-nav-bar">
              {isStaff && (
                <button
                  type="button"
                  onClick={handleCopy}
                  className="post-ref-btn"
                  title="Copy this bleep's reference number"
                >
                  <span className="post-ref-btn__id">{post._id.slice(-6)}</span>
                  <i className="fas fa-copy" aria-hidden="true" />
                  {copyRefText}
                </button>
              )}
              {showDeletePost && (
                <div className="dropdown">
                  <button
                    type="button"
                    className="post-action-btn"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    aria-label="More actions"
                  >
                    <i className="fas fa-ellipsis-v" aria-hidden="true" />
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end post-menu">
                    <li>
                      <button
                        type="button"
                        className="dropdown-item dropdown-item--danger"
                        onClick={deletePost}
                      >
                        <i className="fas fa-trash-alt" aria-hidden="true" />
                        Delete bleep
                      </button>
                    </li>
                  </ul>
                </div>
              )}
              {dismissButton}
            </div>

            {/* The report itself leads, in the slot a listing name would take.
                Deliberately not a heading: it is the reporter's sentence, not a
                name for the page. The hidden h1 keeps the outline valid. */}
            <h1 className="bl-sr-only">
              Suspected counterfeit in {post.category}
              {post.city ? `, ${post.city}` : ""}
            </h1>
            <p className="post-head-desc">{post.description}</p>
            <p
              className={`post-head-price${post.price ? "" : " post-head-price--none"}`}
            >
              {post.price ? `${post.price} UGX` : "Price not given"}
            </p>
            <p className="post-head-meta">
              Reported {relativeTime(post.dateTime)}
              {post.city ? ` in ${post.city}` : ""}
            </p>

            {/* What is happening with the report, in the slot right after what
                the report is. A plain div with a visually hidden label rather
                than a <section aria-label>: a landmark for one status readout
                is noise. */}
            {status && (
              <div className={`post-status post-status--${status.key}`}>
                <span className="bl-sr-only">Report status: </span>
                <div className="post-status__head">
                  <span className="post-status__icon">
                    <i className={status.icon} aria-hidden="true" />
                  </span>
                  <div>
                    <p className="post-status__label">{status.label}</p>
                    <p className="post-status__meaning">{status.meaning}</p>
                  </div>
                </div>
                {/* A pure render of status.step. The status can move backwards
                    (appending Escalated to a resolved ticket flips it back to
                    Ongoing action), so nothing here may animate as though the
                    track only travels one way. */}
                <ol className="post-status__track">
                  {STATUS_STEPS.map((stepLabel, i) => (
                    <li
                      key={stepLabel}
                      className={`post-status__step${
                        i < status.step ? " is-done" : ""
                      }${i === status.step ? " is-current" : ""}`}
                      aria-current={i === status.step ? "step" : undefined}
                    >
                      <span className="post-status__dot" aria-hidden="true" />
                      <span className="post-status__step-label">
                        {stepLabel}
                      </span>
                    </li>
                  ))}
                </ol>
                {/* The offer the block earns: it says where the report stands,
                    so the thing to do next to it is ask to be told when that
                    moves. The sentence is what keeps the button from reading as
                    something dropped in the card. */}
                <div className="post-status__foot">
                  <p className="post-status__foot-label">
                    {post.following == 1
                      ? "You'll be notified when this status changes."
                      : "Follow to be notified when this status changes."}
                  </p>
                  {followButton}
                </div>
              </div>
            )}

            <div className="post-actions post-section">
              <ReactionBar
                post={post}
                onReact={react}
                onShowReactors={() => setShowReactorsModal(true)}
              />

              {/* Only when there is no status block to hold it. */}
              {!status && followButton}

              <button
                type="button"
                className="post-action-btn post-action-btn--end"
                onClick={handleShare}
              >
                <i className="fas fa-share-alt" aria-hidden="true" />
                {shareLabel}
              </button>

              {showMarkResolved && (
                <button
                  type="button"
                  className="post-action-btn resolve"
                  onClick={handleShowConfirmModal}
                >
                  <i className="fas fa-check-circle" aria-hidden="true" />
                  Mark resolved
                </button>
              )}
            </div>

            <hr className="post-divider" />

            <div className="post-section">
              <h2 className="post-section-label">Details</h2>
              <div className="post-fields">
                {post.category && (
                  <div>
                    <div className="post-field-label">Category</div>
                    <div className="post-field-value">{post.category}</div>
                  </div>
                )}

                <div>
                  <div className="post-field-label">Purchase address</div>
                  <div className="post-field-value">{post.address}</div>
                </div>

                {hasPin && (
                  <div>
                    <div className="post-field-label">Pinned location</div>
                    {/* The place name reads as this field's value, on the same
                        label-then-value rhythm as Category and Purchase address
                        above. It used to sit under the map as a separate muted
                        line with a pin icon, which repeated the marker and left
                        the field looking unlabelled. */}
                    {post.location?.name && (
                      <div className="post-field-value">
                        {post.location.name}
                      </div>
                    )}
                    {/* A real button, not a div with role and an Enter handler:
                        this way Space works, and it picks up the shared focus
                        ring. Leaflet renders no interactive descendants here
                        because every control is switched off. */}
                    <button
                      type="button"
                      onClick={() => setShowMapModal(true)}
                      className="post-map-thumb"
                      aria-label="Open the pinned location on a larger map"
                    >
                      <MapContainer
                        center={[mapLat, mapLng]}
                        zoom={15}
                        style={{ height: "100%", width: "100%" }}
                        dragging={false}
                        scrollWheelZoom={false}
                        zoomControl={false}
                        attributionControl={false}
                        doubleClickZoom={false}
                        keyboard={false}
                        touchZoom={false}
                      >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <Marker position={[mapLat, mapLng]} icon={orangePin} />
                      </MapContainer>
                      {/* intercepts clicks so Leaflet never gets them */}
                      <span
                        className="post-map-thumb__shield"
                        aria-hidden="true"
                      />
                      <span className="post-map-chip">
                        <i className="fas fa-expand" aria-hidden="true" />
                        See map
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            <hr className="post-divider" />

            <div className="post-section">
              <h2 className="post-section-label">Reported by</h2>
              <div className="post-author-row">
                <div className="post-avatar">
                  <img src={avatar} alt="" />
                </div>
                <div>
                  <div className="post-author-name">{post.displayname}</div>
                  <div className="post-author-date">
                    {convertDateTimeFormat(post.dateTime)}
                  </div>
                </div>
              </div>
            </div>

            {userId === post.user && (
              <div className="post-thanks">
                <div className="post-thanks-icon">
                  <i className="fas fa-champagne-glasses" aria-hidden="true" />
                </div>
                <div>
                  <div className="post-thanks-title">Thanks for BLEEPing!</div>
                  <div className="post-thanks-body">
                    We will start the verification process immediately and keep
                    you posted. Let us create a counterfeit-free marketplace
                    together.
                  </div>
                </div>
              </div>
            )}

            <hr className="post-divider" />

            {/* ------------------------------------------------- Discussion */}
            <div className="post-section">
              <div className="post-comments-heading">
                <i className="fas fa-comments" aria-hidden="true" />
                {commentsCount} Comment{commentsCount !== 1 ? "s" : ""}
              </div>

              {commentsOpen ? (
                <div className="post-composer-row">
                  <div className="post-composer-avatar">
                    {/* Signed out there is no identicon seed, so this falls
                        back to the app icon, which is a real picture and fills
                        the disc rather than sitting inset on it. */}
                    <img
                      src={useravatar || "/images/logo512.png"}
                      className={useravatar ? undefined : "is-photo"}
                      alt=""
                    />
                  </div>
                  {!commentInputVisible ? (
                    <div
                      className="post-comment-placeholder"
                      role="button"
                      tabIndex={0}
                      onClick={handleToggleCommentInput}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleToggleCommentInput()
                      }
                    >
                      Add a comment
                    </div>
                  ) : (
                    <div className="post-composer">
                      <textarea
                        className="comment-input"
                        ref={composerRef}
                        autoFocus
                        rows={3}
                        placeholder="Share what you know about this bleep"
                        onChange={(e) => setComment(e.target.value)}
                        value={comment}
                      />
                      <div className="post-composer-foot">
                        <div>
                          <button
                            type="button"
                            className="comment-file-btn"
                            onClick={() =>
                              document.getElementById("fileInput").click()
                            }
                          >
                            <i
                              className="fas fa-paperclip"
                              aria-hidden="true"
                            />
                            Attach
                            <input
                              id="fileInput"
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleImageSelection}
                              style={{ display: "none" }}
                            />
                          </button>
                          {imageError && (
                            <span className="post-attach-error">
                              {imageError}
                            </span>
                          )}
                          {selectedImages.length > 0 && (
                            <div className="post-attachments">
                              {selectedImages.map((image, index) => (
                                <div
                                  key={`${image.name}-${index}`}
                                  className="post-attachment"
                                >
                                  <img
                                    src={URL.createObjectURL(image)}
                                    alt={image.name}
                                  />
                                  <button
                                    type="button"
                                    className="post-attachment__remove"
                                    onClick={() => handleRemoveImage(index)}
                                    aria-label={`Remove ${image.name}`}
                                  >
                                    <i
                                      className="fas fa-times"
                                      aria-hidden="true"
                                    />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            type="button"
                            className="comment-cancel-btn"
                            onClick={handleToggleCommentInput}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="comment-post-btn"
                            onClick={postComment}
                            disabled={!comment.trim()}
                          >
                            Comment
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="comments-closed" style={{ marginBottom: 20 }}>
                  <i className="fas fa-ban" aria-hidden="true" /> Comments are
                  now closed for this bleep
                </div>
              )}

              {comments?.length > 0 ? (
                <div id="bleepThread">
                  {comments.map((c) => (
                    <Thread
                      key={c._id}
                      comment={c}
                      handleUpdateComments={fetchComments}
                      handleShowEmailAuthModal={handleShowSignIn}
                      postStatus={post.status}
                      replies={replies.filter(
                        (reply) => reply.thread === c._id,
                      )}
                      userId={userId}
                    />
                  ))}
                </div>
              ) : (
                /* Only while the discussion is open. On a resolved post with no
                   comments this was still inviting you to "Be the first to
                   comment" and then telling you that you could not, directly
                   under the notice that had already said so. The closed notice
                   above is the whole answer in that case. */
                commentsOpen && (
                  <div className="post-comments-empty">
                    <p className="post-comments-empty__title">
                      Be the first to comment
                    </p>
                    <p className="post-comments-empty__sub">
                      Pick one to get started, or write your own
                    </p>
                    <HeroBubbles
                      variant="chat"
                      labels={COMMENT_STARTERS}
                      onSelect={applyStarter}
                    />
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="post-toasts">
        <Toast
          show={showCommentPostedToast}
          onClose={() => setShowCommentPostedToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Body>Your comment has been posted!</Toast.Body>
        </Toast>
        <Toast
          show={showCommentDeletedToast}
          onClose={() => setShowCommentDeletedToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Body>Comment deleted.</Toast.Body>
        </Toast>
      </div>

      <ConfirmModal
        show={showConfirmModal}
        handleClose={handleCloseConfirmModal}
        handleProceed={handleProceed}
        confirmMessage="Are you sure you want to mark this bleep as resolved? This cannot be undone."
      />

      <ReactionsModal
        show={showReactorsModal}
        onHide={() => setShowReactorsModal(false)}
        postId={post._id}
        reactions={post.reactions}
      />

      {hasPin && (
        <Modal
          show={showMapModal}
          onHide={() => setShowMapModal(false)}
          centered
          size="lg"
          contentClassName="bleep-light bl-page--dark post-modal"
        >
          {/* Bootstrap's close glyph is near-black, which disappears on the
              dark modal surface. */}
          <Modal.Header closeVariant="white" closeButton>
            <Modal.Title className="post-modal__title">
              <span className="post-modal__icon">
                <i className="fas fa-map-marker-alt" aria-hidden="true" />
              </span>
              <span className="post-modal__titles">
                {/* The heading names the modal and does not change; the place
                    goes underneath. Titling the whole modal with the place name
                    meant it had no stable identity, and read as a heading for
                    the address rather than for the map. */}
                <span className="post-modal__heading">Pinned location</span>
                <span className="post-modal__sub">
                  {post.location?.name || post.address || "Reported location"}
                </span>
              </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="post-modal__body">
            {showMapModal && (
              <MapContainer
                center={[mapLat, mapLng]}
                zoom={16}
                className="post-modal__map"
                scrollWheelZoom
                zoomControl
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <Marker position={[mapLat, mapLng]} icon={orangePin} />
              </MapContainer>
            )}
          </Modal.Body>
          <Modal.Footer className="post-modal__foot">
            {/* The one thing a reader is likely to want next and could not do
                from here: take the pin somewhere that gives directions. */}
            <a
              className="post-modal__link"
              href={`https://www.google.com/maps/search/?api=1&query=${mapLat},${mapLng}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Google Maps
              <i
                className="fas fa-arrow-up-right-from-square"
                aria-hidden="true"
              />
            </a>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default Post;
